import P from 'prop-types';
import { Fragment, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { Box, CenteredContainer } from '@nerdwallet/currency';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';

import { useBuilder } from '~/app/lib/redux/selectors/builder-selector';
import { COMPONENT_UPDATED } from '~/app/lib/redux/actions/action-types';
import styles from './grid.module.scss';
import Row from '../row/row';
import UniversalText from '../universal-text/universal-text';
import UniversalContainer, {
  universalContainerPropTypes,
} from '../universal-container';
import { isInBuilder } from '../../lib/helpers';

import GridItems from './grid-items';

import editingConfig from './lib/editing-config';

export const updateComponent = (component) => ({
  type: COMPONENT_UPDATED,
  component,
});

const defaultProps = {
  gridItems: [
    {
      $unique_key: new Date().getTime(),
    },
  ],
};

const Grid = ({
  container,
  title,
  subtitle,
  centeredInParentBackground,
  gridItems,
  columns,
  gap,
  centeredInParent,
  disableDefaultProps,
  untouched,
  componentId,
  floatingGrid,
  floatTop,
  floatLeft,
  floatRight,
  floatBottom,
  floatIndex,
  isCopy,
  fitWidth,
  doNotWrapRow,
  justifyContent,
}) => {
  const dispatch = useDispatch();
  const { flatOutline, selected: selectedComponentId } = useBuilder();
  const currentComponent = flatOutline?.[selectedComponentId];
  useEffect(() => {
    if (untouched && selectedComponentId === componentId && !isCopy) {
      dispatch(
        updateComponent({
          ...currentComponent,
          props: { ...defaultProps, untouched: false },
        })
      );
    }
  }, [
    componentId,
    currentComponent,
    untouched,
    isCopy,
    selectedComponentId,
    dispatch,
  ]);

  const pathname = usePathname();
  const isBuilderPage = isInBuilder(pathname);

  const outputFloatingGridTransparency =
    floatingGrid && !disableDefaultProps && isBuilderPage;
  const isGridInEditMode = selectedComponentId === componentId;
  const outputDashedBorder =
    !disableDefaultProps && isBuilderPage && isGridInEditMode;
  const outputGridItemIndicator =
    !disableDefaultProps && isBuilderPage && isGridInEditMode;

  const floatingGridStyle = floatingGrid
    ? {
        position: 'absolute',
        // We calling .replace() for old pages backwards compatibility
        top: `${floatTop?.replace('px', '')}px` || 0,
        bottom: `${floatBottom?.replace('px', '')}px` || 0,
        left: `${floatLeft?.replace('px', '')}px` || 0,
        right: `${floatRight?.replace('px', '')}px` || 0,
        zIndex: floatIndex,
      }
    : null;

  const content = (
    <Fragment>
      {title && <UniversalText {...title} data-testid="grid-title" />}
      {subtitle && (
        <UniversalText
          {...subtitle}
          defaultOptions={{ text: 'text' }}
          data-testid="grid-subtitle"
        />
      )}

      <div>
        <Row
          columns={columns}
          style={{
            ...(gap && { gap: `${gap}px` }),
            ...(doNotWrapRow && { flexWrap: 'nowrap' }),
            ...(justifyContent && { justifyContent }),
          }}
          className={classNames([
            {
              [styles.itemComponentTransparency]:
                outputFloatingGridTransparency,
            },
          ])}
        >
          <GridItems
            outputGridItemIndicator={outputGridItemIndicator}
            outputDashedBorder={outputDashedBorder}
            isBuilderPage={isBuilderPage}
            items={gridItems}
            disableDefaultProps={disableDefaultProps}
            fitWidth={fitWidth}
          />
        </Row>
      </div>
    </Fragment>
  );

  return (
    <Box position="relative">
      <UniversalContainer
        {...container}
        style={{ ...(floatingGrid && floatingGridStyle) }}
        data-testid="grid-root-container"
      >
        {centeredInParent ? (
          <CenteredContainer>
            <UniversalContainer {...centeredInParentBackground}>
              {content}
            </UniversalContainer>
          </CenteredContainer>
        ) : (
          content
        )}
      </UniversalContainer>
    </Box>
  );
};

Grid.displayName = 'Grid';

Grid.propTypes = {
  container: universalContainerPropTypes,
  title: UniversalText.propTypes,
  subtitle: UniversalText.propTypes,
  centeredInParentBackground: P.object,
  columns: P.string,
  gridItems: P.array,
  gap: P.array,
  centeredInParent: P.bool,
  disableDefaultProps: P.bool,
  untouched: P.bool,
  componentId: P.string,
  floatingGrid: P.bool,
  floatTop: P.string,
  floatLeft: P.string,
  floatRight: P.string,
  floatBottom: P.string,
  floatIndex: P.number,
  isCopy: P.object,
  fitWidth: P.bool,
  doNotWrapRow: P.bool,
  justifyContent: P.string,
};

Grid.defaultProps = {
  container: {},
  centeredInParentBackground: {},
  columns: '1',
  gridItems: [],
  gap: '0',
  centeredInParent: false,
  disableDefaultProps: false,
  floatingGrid: false,
  floatTop: '0',
  floatLeft: '0',
  floatRight: '0',
  floatBottom: '0',
  floatIndex: 1,
  fitWidth: false,
  doNotWrapRow: false,
  justifyContent: 'space-between',
};

Grid.getEditingConfig = editingConfig;

export default Grid;
